import { useState, useRef, useCallback, useEffect } from 'react';

export const useChatScroll = (isFloatingBall: boolean = false) => {
  const [userScrolled, setUserScrolled] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollHeightRef = useRef<number>(0);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isTypingRef = useRef(false);
  const initialScrollDoneRef = useRef(false);

  const scrollToBottom = useCallback((force: boolean = false) => {
    if (!force && userScrolled) return;
    
    const container = messagesContainerRef.current;
    if (container) {
      isScrollingRef.current = true;
      
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        });
        isScrollingRef.current = false;
      }, 100);
    }
  }, [userScrolled]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom =
      Math.abs(
        element.scrollHeight - element.scrollTop - element.clientHeight
      ) < 10;

    // Se o usuário estiver rolando para cima e não estiver no topo
    if (element.scrollTop < lastScrollHeightRef.current && element.scrollTop > 0) {
      setUserScrolled(true);
    }

    // Se o usuário estiver no topo ou no final, permite o scroll automático
    if (isAtBottom || element.scrollTop === 0) {
      setUserScrolled(false);
    }

    lastScrollHeightRef.current = element.scrollTop;
  }, []);

  const resetUserScrolled = useCallback(() => {
    setUserScrolled(false);
  }, []);

  const setTyping = useCallback((isTyping: boolean) => {
    isTypingRef.current = isTyping;
    if (isTyping) {
      scrollToBottom(true);
    }
  }, [scrollToBottom]);

  useEffect(() => {
    if (isFloatingBall && !initialScrollDoneRef.current) {
      scrollToBottom(true);
      initialScrollDoneRef.current = true;
    } else if (!isFloatingBall) {
      initialScrollDoneRef.current = false;
    }
  }, [isFloatingBall, scrollToBottom]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return {
    userScrolled,
    messagesContainerRef,
    scrollToBottom,
    handleScroll,
    resetUserScrolled,
    setTyping,
  };
}; 